import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, projectScope, SymbolHit, CallEdge, ModuleDependencyEdge } from "../api/client";
import { pagesApi } from "../api/pagesApi";
import { graphApi } from "../api/graphApi";
import { ForceGraph3DWrapper } from "../graph3d/ForceGraph3DWrapper";
import { DetailDrawer } from "../components/DetailDrawer";
import { useFilterContext } from "../context/FilterContext";
import type { GraphData, GraphNode } from "../types/graph";
import type { CommunityDetail } from "../types/pages";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Modal from "@mui/material/Modal";
import CustomButton from "../components/CustomButton";

type Panel = "symbols" | "modules";

export function RepoExplorer() {
  const { projectId = "", repoId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const { filters, setFilters } = useFilterContext();

  // Local full graph for symbols tab fallback (avoids GraphProvider dependency)
  const [fullGraphData, setFullGraphData] = useState<GraphData | null>(null);

  const [panel, setPanel] = useState<Panel>("symbols");

  // Symbol search state
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<SymbolHit[]>([]);
  const [selected, setSelected] = useState<SymbolHit | null>(null);
  const [edges, setEdges] = useState<CallEdge[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  // Module imports state
  const [moduleEdges, setModuleEdges] = useState<ModuleDependencyEdge[]>([]);
  const [modulePath, setModulePath] = useState(searchParams.get("module") || "");
  const [moduleImports, setModuleImports] = useState<{ target: string }[]>([]);
  const [moduleLoading, setModuleLoading] = useState(false);
  const [moduleErr, setModuleErr] = useState<string | null>(null);

  // Community membership lookup: module_path -> community_id
  const [communityMap, setCommunityMap] = useState<Map<string, number>>(new Map());

  // Selected graph node for detail drawer
  const [drawerNode, setDrawerNode] = useState<GraphNode | null>(null);
  const [expanded, setExpanded] = useState(false);

  // URL param: ?highlight=id1,id2  (from blast-radius "View in 3D" button)
  const urlHighlights = useMemo(() => {
    const h = searchParams.get("highlight");
    if (!h) return new Set<string>();
    return new Set(h.split(",").filter(Boolean));
  }, [searchParams]);

  // When highlights are present, fetch symbol details and populate the search panel
  useEffect(() => {
    if (urlHighlights.size === 0 || !repoId) return;
    const ids = Array.from(urlHighlights);
    Promise.all(
      ids.map((id) =>
        api.get<SymbolHit>(projectScope(`/repos/${repoId}/symbols/${id}`, projectId)).catch(() => null)
      )
    ).then((results) => {
      const valid = results.filter((r): r is SymbolHit => r !== null);
      if (valid.length > 0) { setHits(valid);}
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoId]);

  // URL param: ?community=N  (from CommunitiesTab row click)
  useEffect(() => {
    const communityId = searchParams.get("community");
    if (!communityId) return;
    const cid = Number(communityId);
    if (!Number.isNaN(cid)) {
      setFilters({ languages: [], kinds: [], communityIds: [cid], edgeTypes: [], crossServiceOnly: false });
      setPanel("modules");

      // Fetch community data to build module_path -> community_id mapping
      pagesApi.getCommunities(repoId, projectId).then((data) => {
        const map = new Map<string, number>();
        data.communities.forEach((c: CommunityDetail) => {
          c.members.forEach((path) => map.set(path, c.id));
        });
        setCommunityMap(map);
      }).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL param: ?focus=symbolId OR ?symbol=symbolId  (from hotspot/ADR links)
  useEffect(() => {
    const focusId = searchParams.get("focus") || searchParams.get("symbol");
    if (!focusId || !repoId) return;
    api.get<SymbolHit[]>(projectScope(`/repos/${repoId}/symbols?q=${encodeURIComponent(focusId)}&limit=20`, projectId))
      .then((results) => {
        const match = results.find((h) => h.id === focusId) ?? results[0];
        if (match) {
          setSelected(match);
          setHits(results);
          setQ(match.fq_name);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoId]);

  // Ensure full graph is loaded for symbols tab fallback (community-filtered view)
  useEffect(() => {
    if (repoId && !fullGraphData) {
      graphApi.getFullGraph(repoId, projectId).then(setFullGraphData).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoId]);

  async function search() {
    setErr(null);
    setSearching(true);
    try {
      const list = await api.get<SymbolHit[]>(projectScope(`/repos/${repoId}/symbols?q=${encodeURIComponent(q)}&limit=50`, projectId));
      setHits(list);
    } catch (e) { setErr((e as Error).message); }
    finally { setSearching(false); }
  }

  useEffect(() => {
    if (!selected) { setEdges([]); return; }
    (async () => {
      try {
        const callers = await api.get<CallEdge[]>(projectScope(`/repos/${repoId}/symbols/${selected.id}/callers?depth=2`, projectId));
        const callees = await api.get<CallEdge[]>(projectScope(`/repos/${repoId}/symbols/${selected.id}/callees?depth=2`, projectId));
        setEdges([...callers, ...callees]);
      } catch (e) { setErr((e as Error).message); }
    })();
  }, [selected, repoId]);

  // Load module dependency graph when modules panel is activated
  useEffect(() => {
    if (panel !== "modules" || moduleEdges.length > 0) return;
    (async () => {
      try {
        const list = await api.get<ModuleDependencyEdge[]>(projectScope(`/repos/${repoId}/graph/modules`, projectId));
        setModuleEdges(list);
      } catch (e) { setModuleErr((e as Error).message); }
    })();
  }, [panel, repoId, moduleEdges.length]);

  async function fetchModuleImports() {
    if (!modulePath.trim()) return;
    setModuleLoading(true);
    setModuleErr(null);
    try {
      const list = await api.get<{ target: string }[]>(
        projectScope(`/repos/${repoId}/graph/modules/${encodeURIComponent(modulePath.trim())}/imports`, projectId)
      );
      setModuleImports(list);
    } catch (e) { setModuleErr((e as Error).message); setModuleImports([]); }
    finally { setModuleLoading(false); }
  }

  const modulePaths = useMemo(
    () => Array.from(new Set([...moduleEdges.map((e) => e.source), ...moduleEdges.map((e) => e.target)])).sort(),
    [moduleEdges]
  );

  // Community-filtered module edges for display in both graph and sidebar table
  const filteredModuleEdges = useMemo(() => {
    if (filters.communityIds.length === 0 || communityMap.size === 0) return moduleEdges;
    const communityModules = new Set(
      Array.from(communityMap.entries())
        .filter(([, cid]) => filters.communityIds.includes(cid))
        .map(([path]) => path)
    );
    return moduleEdges.filter(
      (e) => communityModules.has(e.source) || communityModules.has(e.target)
    );
  }, [moduleEdges, filters.communityIds, communityMap]);

  const graphData: GraphData = useMemo(() => {
    if (panel === "modules" && filteredModuleEdges.length > 0) {
      const focus = modulePath.trim();
      const edgesToRender = focus
        ? filteredModuleEdges.filter((e) => e.source === focus || e.target === focus)
        : filteredModuleEdges.slice(0, 500);

      const nodeMap = new Map<string, GraphNode>();
      const fanIn = new Map<string, number>();
      const fanOut = new Map<string, number>();
      edgesToRender.forEach((e) => {
        fanOut.set(e.source, (fanOut.get(e.source) ?? 0) + 1);
        fanIn.set(e.target, (fanIn.get(e.target) ?? 0) + 1);
      });

      edgesToRender.forEach((e) => {
        for (const path of [e.source, e.target]) {
          if (!nodeMap.has(path)) {
            const segments = path.replace(/\\/g, "/").split("/");
            const name = segments[segments.length - 1] || path;
            nodeMap.set(path, {
              id: path, name, fq_name: path, kind: "Module", file_path: path,
              start_line: null, end_line: null, language: null, community_id: communityMap.get(path) ?? null,
              fan_in: fanIn.get(path) ?? 0, fan_out: fanOut.get(path) ?? 0,
            });
          }
        }
      });

      return {
        nodes: Array.from(nodeMap.values()),
        edges: edgesToRender.map((e, i) => ({
          id: `me${i}`, source: e.source, target: e.target, type: "IMPORTS" as const,
        })),
      };
    }

    const nodeMap = new Map<string, GraphNode>();
    if (selected) {
      nodeMap.set(selected.id, {
        id: selected.id,
        name: selected.fq_name.split(".").pop() ?? selected.fq_name,
        fq_name: selected.fq_name,
        kind: (selected.kind.charAt(0).toUpperCase() + selected.kind.slice(1)) as GraphNode["kind"],
        file_path: selected.file_path,
        start_line: selected.start_line ?? null,
        end_line: selected.end_line ?? null,
        language: selected.language ?? null,
        community_id: null, fan_in: 0, fan_out: 0,
      });
    }
    edges.forEach((e) => {
      for (const [id] of [[e.from_id], [e.to_id]]) {
        if (!nodeMap.has(id)) {
          nodeMap.set(id, { id, name: id, fq_name: id, kind: "Function", file_path: "", start_line: null, end_line: null, language: null, community_id: null, fan_in: 0, fan_out: 0 });
        }
      }
    });

    // If no symbol selected and no edges, fall back to full graph (respects filter context)
    if (nodeMap.size === 0 && fullGraphData && fullGraphData.nodes.length > 0) {
      return fullGraphData;
    }

    return {
      nodes: Array.from(nodeMap.values()),
      edges: edges.map((e, i) => ({
        id: `e${i}`, source: e.from_id, target: e.to_id,
        type: (e.kind === "calls" ? "CALLS" : e.kind === "imports" ? "IMPORTS" : "CALLS") as GraphData["edges"][number]["type"],
      })),
    };
  }, [edges, selected, panel, filteredModuleEdges, modulePath, communityMap, fullGraphData]);

  return (
    <Box className="explorer-layout">
      {/* Left panel: search or modules */}
      <Box component="aside" className="explorer-aside">
        {/* Panel tabs */}
        <Box className="explorer-tabs">
          <button
            className={`explorer-tab ${panel === "symbols" ? "active" : ""}`}
            onClick={() => setPanel("symbols")}
          >
            Symbols
          </button>
          <button
            className={`explorer-tab ${panel === "modules" ? "active" : ""}`}
            onClick={() => setPanel("modules")}
          >
            Modules
          </button>
        </Box>

        {panel === "symbols" && (
          <Box className="explorer-panel-body">
            <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
              <TextField
                placeholder="Search symbols"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                size="small"
                fullWidth
                className="login-input"
              />
              <CustomButton
                loading={searching}
                buttonText="Go"
                disabled={!q}
                handleSubmitClick={search}
                width="auto"
                minWidth="48px"
                padding="6px 14px"
              />
            </Box>
            {err && <Alert severity="error" sx={{ mb: 1, py: 0.25 }}>{err}</Alert>}
            <Box className="explorer-results">
              {selected ? (
                <Box>
                  <Box sx={{ display: "flex", gap: 0.5, mb: 1 }}>
                    <Chip label={selected.kind} size="small" className="explorer-chip" />
                    {selected.language && <Chip label={selected.language} size="small" className="explorer-chip" />}
                  </Box>
                  <Typography variant="caption" className="explorer-filepath">
                    {selected.file_path}:{selected.start_line ?? "?"}-{selected.end_line ?? "?"}
                  </Typography>
                  <CustomButton
                    loading={false}
                    buttonText={<><img src="/arrow_right.png" alt="" style={{ width: 14, height: 14, verticalAlign: "middle", marginRight: 4, transform: "scaleX(-1)" }} /> Back to results</>}
                    buttonTextLoading=""
                    handleSubmitClick={() => setSelected(null)}
                    background="transparent"
                    color="rgba(74, 30, 71, 1)"
                    border="1px solid rgba(203, 46, 186, 1)"
                    hoverBgColor="rgba(203, 46, 186, 0.08)"
                  />
                </Box>
              ) : (
                <Box component="ul" className="explorer-hit-list">
                  {hits.map((h) => (
                    <Box component="li" key={h.id} className="explorer-hit-item">
                      <a href="#" onClick={(e) => { e.preventDefault(); setSelected(h); }} className="explorer-hit-link">
                        {h.fq_name}
                      </a>
                      <Typography variant="caption" className="explorer-hit-meta">
                        {h.kind} · {h.file_path}
                      </Typography>
                    </Box>
                  ))}
                  {hits.length === 0 && (
                    <Typography variant="body2" sx={{ color: "rgba(120,120,125,1)", py: 1 }}>
                      No results.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {panel === "modules" && (
          <Box className="explorer-panel-body">
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <TextField
                placeholder="Module path (e.g. app/main.py)"
                value={modulePath}
                onChange={(e) => setModulePath(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchModuleImports()}
                size="small"
                fullWidth
                className="login-input"
                slotProps={{ htmlInput: { list: "module-paths-list" } }}
              />
              <CustomButton
                loading={moduleLoading}
                disabled={!modulePath.trim()}
                buttonText="Go"
                buttonTextLoading="Go"
                handleSubmitClick={fetchModuleImports}
                width="auto"
                minWidth="48px"
                padding="6px 14px"
              />
            </Box>
            <datalist id="module-paths-list">
              {modulePaths.slice(0, 100).map((p) => <option key={p} value={p} />)}
            </datalist>
            {moduleErr && <Alert severity="error" sx={{ mb: 1, py: 0.25 }}>{moduleErr}</Alert>}
            <Box className="explorer-results">
              {moduleImports.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" className="explorer-section-title">
                    Imports of <code className="explorer-code">{modulePath}</code>
                  </Typography>
                  <Box component="ul" className="explorer-hit-list">
                    {moduleImports.map((imp) => (
                      <Box component="li" key={imp.target} className="explorer-hit-item">
                        <a href="#" onClick={(e) => { e.preventDefault(); setModulePath(imp.target); if (filters.communityIds.length > 0) setFilters({ ...filters, communityIds: [] }); }} className="explorer-hit-link" style={{ fontFamily: "monospace", fontSize: 13 }}>
                          {imp.target}
                        </a>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              {filteredModuleEdges.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" className="explorer-section-title">
                    Dependency graph ({filteredModuleEdges.length} edges)
                  </Typography>
                  <Box className="explorer-dep-table-wrap">
                    <table className="explorer-dep-table">
                      <thead><tr><th>Source</th><th>Target</th></tr></thead>
                      <tbody>
                        {filteredModuleEdges.slice(0, 100).map((e, i) => (
                          <tr key={i}>
                            <td>
                              <a href="#" onClick={(ev) => { ev.preventDefault(); setModulePath(e.source); if (filters.communityIds.length > 0) setFilters({ ...filters, communityIds: [] }); }}>{e.source}</a>
                            </td>
                            <td>
                              <a href="#" onClick={(ev) => { ev.preventDefault(); setModulePath(e.target); if (filters.communityIds.length > 0) setFilters({ ...filters, communityIds: [] }); }}>{e.target}</a>
                            </td>
                          </tr>
                        ))}
                        {filteredModuleEdges.length > 100 && (
                          <tr><td colSpan={2} style={{ color: "rgba(120,120,125,1)" }}>Showing first 100 of {filteredModuleEdges.length} edges</td></tr>
                        )}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* 3D graph */}
      <Box sx={{ flex: 1, position: "relative", background: "#ffffff", overflow: "hidden" }}>
        <ForceGraph3DWrapper
          graphData={graphData}
          filters={filters}
          selectedNodeId={panel === "modules" ? (modulePath.trim() || null) : (selected?.id ?? null)}
          searchHighlights={urlHighlights}
          colorMode="kind"
          onNodeClick={(node) => {
            if (panel === "modules") {
              setModulePath(node.id);
            } else {
              const hit = hits.find((h) => h.id === node.id);
              if (hit) setSelected(hit);
            }
            setDrawerNode(node);
          }}
          onNodeDoubleClick={() => {}}
          onBackgroundClick={() => setDrawerNode(null)}
        />
        <Box sx={{ position: "absolute", top: 16, right: 16, zIndex: 9999, pointerEvents: "auto" }}>
          <button
            className="explorer-expand-btn"
            onClick={() => setExpanded(!expanded)}
            title={expanded ? "Collapse" : "Expand explorer"}
          >
            ⛶
          </button>
        </Box>
      </Box>

      {/* Expanded modal — full explorer (panel + canvas) */}
      <Modal open={expanded} onClose={() => setExpanded(false)}>
        <Box className="explorer-expanded-modal-fullscreen" sx={{ display: "flex" }}>
          {/* Left panel in modal */}
          <Box sx={{ width: 320, minWidth: 320, borderRight: "1px solid #e3e3e5", overflow: "auto", background: "#ffffff" }}>
            <Box className="explorer-tabs">
              <button className={`explorer-tab ${panel === "symbols" ? "active" : ""}`} onClick={() => setPanel("symbols")}>Symbols</button>
              <button className={`explorer-tab ${panel === "modules" ? "active" : ""}`} onClick={() => setPanel("modules")}>Modules</button>
            </Box>
            <Box sx={{ p: 1.5 }}>
              {panel === "symbols" && (
                <>
                  <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                    <TextField placeholder="Search symbols" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} size="small" fullWidth className="login-input" />
                    <CustomButton loading={searching} buttonText="Go" buttonTextLoading="Go" handleSubmitClick={search} width="auto" minWidth="48px" padding="6px 14px" />
                  </Box>
                  <Box className="explorer-results">
                    {selected ? (
                      <Box>
                        <Box sx={{ display: "flex", gap: 0.5, mb: 1 }}>
                          <Chip label={selected.kind} size="small" className="explorer-chip" />
                          {selected.language && <Chip label={selected.language} size="small" className="explorer-chip" />}
                        </Box>
                        <Typography variant="caption" className="explorer-filepath">{selected.file_path}:{selected.start_line ?? "?"}-{selected.end_line ?? "?"}</Typography>
                        <CustomButton loading={false} buttonText={<>← Back</>} buttonTextLoading="" handleSubmitClick={() => setSelected(null)} />
                      </Box>
                    ) : (
                      <Box component="ul" className="explorer-hit-list">
                        {hits.map((h) => (
                          <Box component="li" key={h.id} className="explorer-hit-item">
                            <a href="#" onClick={(e) => { e.preventDefault(); setSelected(h); }} className="explorer-hit-link">{h.fq_name}</a>
                            <Typography variant="caption" className="explorer-hit-meta">{h.kind} · {h.file_path}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </>
              )}
              {panel === "modules" && (
                <>
                  <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                    <TextField placeholder="Module path" value={modulePath} onChange={(e) => setModulePath(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchModuleImports()} size="small" fullWidth className="login-input" />
                    <CustomButton loading={moduleLoading} disabled={!modulePath.trim()} buttonText="Go" buttonTextLoading="Go" handleSubmitClick={fetchModuleImports} width="auto" minWidth="48px" padding="6px 14px" />
                  </Box>
                  {filteredModuleEdges.length > 0 && (
                    <Box sx={{ maxHeight: "calc(100vh - 200px)", overflow: "auto" }}>
                      <Typography variant="subtitle2" className="explorer-section-title">Dependency graph ({filteredModuleEdges.length} edges)</Typography>
                      <table className="explorer-dep-table">
                        <thead><tr><th>Source</th><th>Target</th></tr></thead>
                        <tbody>
                          {filteredModuleEdges.map((e, i) => (
                            <tr key={i}>
                              <td><a href="#" onClick={(ev) => { ev.preventDefault(); setModulePath(e.source); if (filters.communityIds.length > 0) setFilters({ ...filters, communityIds: [] }); }}>{e.source}</a></td>
                              <td><a href="#" onClick={(ev) => { ev.preventDefault(); setModulePath(e.target); if (filters.communityIds.length > 0) setFilters({ ...filters, communityIds: [] }); }}>{e.target}</a></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
          {/* Canvas in modal */}
          <Box sx={{ flex: 1, position: "relative", background: "#ffffff", overflow: "hidden" }}>
            <ForceGraph3DWrapper
              graphData={graphData}
              filters={filters}
              selectedNodeId={panel === "modules" ? (modulePath.trim() || null) : (selected?.id ?? null)}
              searchHighlights={urlHighlights}
              colorMode="kind"
              onNodeClick={(node) => {
                if (panel === "modules") { setModulePath(node.id); } else { const hit = hits.find((h) => h.id === node.id); if (hit) setSelected(hit); }
                setDrawerNode(node);
              }}
              onNodeDoubleClick={() => {}}
              onBackgroundClick={() => setDrawerNode(null)}
            />
          </Box>
        </Box>
      </Modal>

      <DetailDrawer node={drawerNode} onClose={() => setDrawerNode(null)} />
    </Box>
  );
}
